// src/components/Inventory/index.js
import React, { useState, useEffect, useContext, useCallback } from "react";
import { useNavigate, Outlet, useLocation } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { UserContext } from "../../UserContext";
import "./style.css";
import axios from "axios";

const InventoryLayout = () => {
  const navigate = useNavigate();
  const [t] = useTranslation();
  const location = useLocation();
  const { token, userData } = useContext(UserContext);
  const [warehouses, setWarehouses] = useState([]);
  const farmId = userData.Employee?.farmId;

  const getActiveTabFromLocation = useCallback(() => {
    const pathSegments = location.pathname.split("/");
    return pathSegments[pathSegments.length - 1] || "warehouses";
  }, [location.pathname]);

  const [activeTab, setActiveTab] = useState(getActiveTabFromLocation());

  useEffect(() => {
    if (location.pathname === "/inventory") {
      navigate("/inventory/warehouses");
    }
    setActiveTab(getActiveTabFromLocation());
  }, [location.pathname, navigate, getActiveTabFromLocation]);

  useEffect(() => {
    const fetchWarehouses = async () => {
      if (!farmId) return;
      try {
        const response = await axios.get(
          `api/inventoryLocation/findByFarmId/${farmId}`,
          {
            headers: { Authorization: `${token}` },
          }
        );
        console.log("warehouses: ", response.data.items);
        setWarehouses(response.data.items || []);
      } catch (error) {
        console.error("Error fetching warehouses:", error);
      }
    };

    fetchWarehouses();
  }, [farmId, token]);

  // TEST: Fetch inventory statistics (same API as Android app)
  useEffect(() => {
    const fetchInventoryStatistics = async () => {
      if (!farmId) return;

      // Android app sends empty strings initially, or timestamp strings when filtered
      // Let's try both approaches

      // Test 1: Empty strings (like Android does on initial load)
      console.log('=== TEST 1: EMPTY DATE STRINGS (Android initial load) ===');
      console.log('Endpoint: /inventoryItemDetails/getInventoryReport/{farmId}');
      console.log('Farm ID:', farmId);
      console.log('Start Date: "" (empty string)');
      console.log('End Date: "" (empty string)');

      try {
        const response1 = await axios.get(
          `api/inventoryItemDetails/getInventoryReport/${farmId}`,
          {
            headers: { Authorization: `${token}` },
            params: {
              startDate: "",
              endDate: ""
            }
          }
        );

        console.log('=== RESPONSE WITH EMPTY DATES ===');
        console.log('Status:', response1.status);
        console.log('Full Response:', response1.data);

        if (response1.data.items && response1.data.items.length > 0) {
          console.log('Number of products:', response1.data.items.length);
          console.log('Sample product statistics:');
          response1.data.items.slice(0, 3).forEach((item, index) => {
            console.log(`Product ${index + 1}:`, {
              productName: item.productName,
              availableQuantity: item.availableQuantity,
              importedQuantity: item.importedQuantity,
              exportedQuantity: item.exportedQuantity,
              inventoryValue: item.inventoryValue,
              threshold: item.threshold,
              categoryAr: item.categoryAr,
              categoryEn: item.categoryEn,
              locationAr: item.locationAr,
              locationEn: item.locationEn
            });
          });
        } else {
          console.log('No inventory statistics found');
        }
      } catch (error) {
        console.error('Error with empty dates:', error.response?.data || error.message);
      }

      // Test 2: Timestamp strings (like Android does when filtering by date)
      console.log('\n=== TEST 2: TIMESTAMP STRINGS (Android date filter) ===');
      const endTimestamp = Date.now().toString(); // Current timestamp
      const startTimestamp = (Date.now() - 30 * 24 * 60 * 60 * 1000).toString(); // 30 days ago
      console.log('Start Timestamp:', startTimestamp);
      console.log('End Timestamp:', endTimestamp);

      try {
        const response2 = await axios.get(
          `api/inventoryItemDetails/getInventoryReport/${farmId}`,
          {
            headers: { Authorization: `${token}` },
            params: {
              startDate: startTimestamp,
              endDate: endTimestamp
            }
          }
        );

        console.log('=== RESPONSE WITH TIMESTAMPS ===');
        console.log('Status:', response2.status);
        console.log('Full Response:', response2.data);

        if (response2.data.items && response2.data.items.length > 0) {
          console.log('Number of products:', response2.data.items.length);
          console.log('Sample product statistics:');
          response2.data.items.slice(0, 3).forEach((item, index) => {
            console.log(`Product ${index + 1}:`, {
              productName: item.productName,
              availableQuantity: item.availableQuantity,
              importedQuantity: item.importedQuantity,
              exportedQuantity: item.exportedQuantity,
              inventoryValue: item.inventoryValue,
              threshold: item.threshold,
              categoryAr: item.categoryAr,
              categoryEn: item.categoryEn,
              locationAr: item.locationAr,
              locationEn: item.locationEn
            });
          });
        } else {
          console.log('No inventory statistics found');
        }
      } catch (error) {
        console.error('Error with timestamps:', error.response?.data || error.message);
      }

      console.log('\n=== END OF INVENTORY STATISTICS TESTS ===');
    };

    // Call the test function
    fetchInventoryStatistics();
  }, [farmId, token]);

  const handleTabChange = (tab) => {
    navigate(`/inventory/${tab}`);
  };

  return (
    <div className="inventory-layout-page">
      <header className="view-header">
        {/* START: MODIFICATION - Group title and new subtitle */}
        <div>
          <h1 className="view-header-title">{t("Inventory Management")}</h1>
          {/* Warehouse count moved here as a subtitle */}
          <p className="view-header-subtitle">
            {warehouses.length} {t("Warehouse")}
            {/* {warehouses.length !== 1 ? t("s") : ""} */}
          </p>
        </div>
        {/* END: MODIFICATION */}
      </header>

      <div className="tabs-container">
        <button
          className={`tab-button ${activeTab === "warehouses" ? "active" : ""}`}
          onClick={() => handleTabChange("warehouses")}
        >
          {t("Warehouses")}
          {/* Warehouse count removed from here */}
        </button>
        <button
          className={`tab-button ${activeTab === "types" ? "active" : ""}`}
          onClick={() => handleTabChange("types")}
        >
          {t("Types")}
        </button>
        <button
          className={`tab-button ${activeTab === "stock" ? "active" : ""}`}
          onClick={() => handleTabChange("stock")}
        >
          {t("Stock Products")}
        </button>
        <button
          className={`tab-button ${activeTab === "usage" ? "active" : ""}`}
          onClick={() => handleTabChange("usage")}
        >
          {t("Usage/Distribution")}
        </button>
      </div>

      <Outlet />
    </div>
  );
};

export default InventoryLayout;
